<template>
    <jetstreem-modal :show="show" :max-width="'md'" @close="$emit('close')">
        <div class="m-5">
            <h2 class="text-left text-xl font-semibold text-gray-900">
                Submit Required Information
            </h2>
            <p class="mt-2 text-left text-sm text-gray-600">
                Select the requirement type you were asked to provide, then upload or enter the requested information.
            </p>

            <form @submit.prevent="submitResubmission" aria-label="Plaid resubmission form">
                <div class="mt-4 text-left">
                    <label for="requirement_type" class="block text-sm font-medium text-gray-700">
                        Requirement Type <span class="text-red-700">*</span>
                    </label>
                    <select
                        id="requirement_type"
                        v-model="form.requirement_type"
                        class="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                        @change="resetInputValues"
                    >
                        <option value="">Select a requirement</option>
                        <option v-for="(field, key) in fields" :key="key" :value="key">
                            {{ field.label }}
                        </option>
                    </select>
                    <p v-if="form.errors.requirement_type" class="mt-1 text-sm text-red-500">
                        {{ form.errors.requirement_type }}
                    </p>
                </div>

                <div v-if="selectedField" class="mt-4 text-left">
                    <p class="text-sm text-gray-600">
                        {{ selectedField.help_text }}
                    </p>

                    <div v-if="selectedField.input_type === 'file'" class="mt-3">
                        <label for="value" class="block text-sm font-medium text-gray-700">
                            Upload Document <span class="text-red-700">*</span>
                        </label>
                        <input
                            id="value"
                            type="file"
                            class="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            @input="form.value = $event.target.files[0]"
                        >
                        <p v-if="form.errors.value" class="mt-1 text-sm text-red-500">
                            {{ form.errors.value }}
                        </p>
                    </div>

                    <div v-else-if="selectedField.input_type === 'select'" class="mt-3">
                        <label for="value" class="block text-sm font-medium text-gray-700">
                            {{ selectedField.label }} <span class="text-red-700">*</span>
                        </label>
                        <select
                            id="value"
                            v-model="form.value"
                            class="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                        >
                            <option value="">Select an option</option>
                            <option v-for="option in selectedField.options" :key="option" :value="option">
                                {{ option }}
                            </option>
                        </select>
                        <p v-if="form.errors.value" class="mt-1 text-sm text-red-500">
                            {{ form.errors.value }}
                        </p>
                    </div>

                    <div v-else-if="selectedField.input_type === 'address'" class="mt-3 space-y-3">
                        <div>
                            <label for="street" class="block text-sm font-medium text-gray-700">
                                Street Address <span class="text-red-700">*</span>
                            </label>
                            <input
                                id="street"
                                v-model="form.address.street"
                                type="text"
                                class="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            >
                            <p v-if="form.errors['address.street']" class="mt-1 text-sm text-red-500">
                                {{ form.errors['address.street'] }}
                            </p>
                        </div>
                        <div>
                            <label for="street2" class="block text-sm font-medium text-gray-700">
                                Address Line 2
                            </label>
                            <input
                                id="street2"
                                v-model="form.address.street2"
                                type="text"
                                class="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            >
                        </div>
                        <div>
                            <label for="city" class="block text-sm font-medium text-gray-700">
                                City <span class="text-red-700">*</span>
                            </label>
                            <input
                                id="city"
                                v-model="form.address.city"
                                type="text"
                                class="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            >
                            <p v-if="form.errors['address.city']" class="mt-1 text-sm text-red-500">
                                {{ form.errors['address.city'] }}
                            </p>
                        </div>
                        <div>
                            <label for="region" class="block text-sm font-medium text-gray-700">
                                State <span class="text-red-700">*</span>
                            </label>
                            <select
                                id="region"
                                v-model="form.address.region"
                                class="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                            >
                                <option value="">Select a state</option>
                                <option v-for="(stateName, stateCode) in states" :key="stateCode" :value="stateCode">
                                    {{ stateName }}
                                </option>
                            </select>
                            <p v-if="form.errors['address.region']" class="mt-1 text-sm text-red-500">
                                {{ form.errors['address.region'] }}
                            </p>
                        </div>
                        <div>
                            <label for="postal_code" class="block text-sm font-medium text-gray-700">
                                ZIP Code <span class="text-red-700">*</span>
                            </label>
                            <input
                                id="postal_code"
                                v-model="form.address.postal_code"
                                type="text"
                                class="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            >
                            <p v-if="form.errors['address.postal_code']" class="mt-1 text-sm text-red-500">
                                {{ form.errors['address.postal_code'] }}
                            </p>
                        </div>
                    </div>

                    <div v-else class="mt-3">
                        <label for="value" class="block text-sm font-medium text-gray-700">
                            {{ selectedField.label }} <span class="text-red-700">*</span>
                        </label>
                        <input
                            id="value"
                            v-model="form.value"
                            type="text"
                            class="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        >
                        <p v-if="form.errors.value" class="mt-1 text-sm text-red-500">
                            {{ form.errors.value }}
                        </p>
                    </div>
                </div>

                <div class="pt-5">
                    <div class="flex justify-end">
                        <button
                            type="button"
                            class="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:ml-3 sm:mt-0 sm:w-auto sm:text-sm"
                            @click="$emit('close')"
                        >
                            Close
                        </button>
                        <form-button :form="form" type="submit" class="ml-2">
                            Submit
                        </form-button>
                    </div>
                </div>
            </form>
        </div>
    </jetstreem-modal>
</template>

<script>
import JetstreemModal from '@/Jetstream/Modal.vue'
import FormButton from '@/Shared/FormButton.vue'

export default {
    components: {
        JetstreemModal,
        FormButton,
    },
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        fields: {
            type: Object,
            default: () => ({}),
        },
        states: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['close'],
    data() {
        return {
            form: this.$inertia.form({
                requirement_type: '',
                value: null,
                address: {
                    street: '',
                    street2: '',
                    city: '',
                    region: '',
                    postal_code: '',
                },
            }),
        }
    },
    computed: {
        selectedField() {
            if (!this.form.requirement_type) {
                return null
            }

            return this.fields[this.form.requirement_type] ?? null
        },
    },
    watch: {
        show(isVisible) {
            if (!isVisible) {
                this.resetForm()
            }
        },
    },
    methods: {
        resetInputValues() {
            this.form.value = null
            this.form.address = {
                street: '',
                street2: '',
                city: '',
                region: '',
                postal_code: '',
            }
            this.form.clearErrors()
        },
        resetForm() {
            this.form.reset()
            this.form.clearErrors()
        },
        submitResubmission() {
            this.form.post(this.route('plaid.resubmission.store'), {
                forceFormData: true,
                onSuccess: () => {
                    this.resetForm()
                    this.$emit('close')
                },
            })
        },
    },
}
</script>
